import React from 'react';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QuizIcon from '@mui/icons-material/Quiz';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  selectedBoard: string;
  selectedClass: string;
  selectedSubject: string;
  selectedTopicSubtopics: Record<string, string[]>;
  selectedDifficulty: string[];
  selectedQuestionTypes: string[];
  selectedQuestionCount: string;
  canGenerate: boolean;
  isGenerating: boolean;
  onGenerate: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  open,
  onClose,
  selectedBoard,
  selectedClass,
  selectedSubject,
  selectedTopicSubtopics,
  selectedDifficulty,
  selectedQuestionTypes,
  selectedQuestionCount,
  canGenerate,
  isGenerating,
  onGenerate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getTotalSelectedTopics = () => Object.keys(selectedTopicSubtopics).length;
  const getTotalSelectedSubtopics = () =>
    Object.values(selectedTopicSubtopics).reduce((sum, subtopics) => sum + subtopics.length, 0);

  const SidebarContent = () => (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontSize: 12, fontWeight: 600 }}>
          Configuration
        </Typography>
        {isMobile && (
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', mb: 0.3 }}>
          Board
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 11 }}>
          {selectedBoard || 'Not selected'}
        </Typography>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', mb: 0.3 }}>
          Class
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 11 }}>
          {selectedClass ? `Class ${selectedClass}` : 'Not selected'}
        </Typography>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', mb: 0.3 }}>
          Subject
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 11 }}>
          {selectedSubject || 'Not selected'}
        </Typography>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', mb: 0.3 }}>
          Topics & Subtopics
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 11 }}>
          {getTotalSelectedTopics() > 0
            ? `${getTotalSelectedTopics()} topics, ${getTotalSelectedSubtopics()} subtopics`
            : 'Not selected'}
        </Typography>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', mb: 0.3 }}>
          Difficulty
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 11 }}>
          {selectedDifficulty.length > 0 ? selectedDifficulty.join(', ') : 'Not selected'}
        </Typography>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', mb: 0.3 }}>
          Question Types
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 11 }}>
          {selectedQuestionTypes.length > 0 ? `${selectedQuestionTypes.length} selected` : 'Not selected'}
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', mb: 0.3 }}>
          Question Count
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 11 }}>
          {selectedQuestionCount || 'Not selected'}
        </Typography>
      </Box>

      <Button
        variant="contained"
        fullWidth
        onClick={onGenerate}
        disabled={!canGenerate || isGenerating}
        startIcon={<QuizIcon />}
        sx={{
          textTransform: 'none',
          fontSize: 12,
          py: 1.2,
          borderRadius: 1.5,
        }}
      >
        {isGenerating ? 'Generating...' : 'Generate Questions'}
      </Button>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 260,
          },
        }}
      >
        <SidebarContent />
      </Drawer>
    );
  }

  return (
    <Box sx={{ width: 260,mt: 2 }}>
      <Paper elevation={1} sx={{ borderRadius: 2 }}>
        <SidebarContent />
      </Paper>
    </Box>
  );
};

export default Sidebar;